import { tracked } from '@glimmer/tracking';
import type { CompanyLpnSettingExtension as CompanyLpnSetting } from 'tio-data/data/resources/company-lpn-setting.ext';
import type { CompanySettingExtension as CompanySetting } from 'tio-data/data/resources/company-setting.ext';
import type { LpnPage } from './lpn-page.type.ts';
export interface LpnPageExtension extends LpnPage {}
export class LpnPageExtension {
  @tracked declare setting?: CompanyLpnSetting;

  @tracked declare isEnabled?: boolean;

  @tracked declare employerRecommended?: boolean;

  @tracked declare hasDiscount?: boolean;

  @tracked declare createdByOtherCompany?: boolean;

  get savingsPercentRange(): string | undefined {
    return this.discountPercentRange;
  }

  set savingsPercentRange(value: string | undefined) {
    this.discountPercentRange = value;
  }

  get savingsAmountRange(): string | undefined {
    return this.saveDollarAmountRange;
  }

  set savingsAmountRange(value: string | undefined) {
    this.saveDollarAmountRange = value;
  }

  /**
   * Find the CompanyLpnSetting for this page and the provided company setting.
   * Returns `undefined` if none exists.
   *
   * Resolves only from linkage already in the cache, established from either
   * direction: including `company-lpn-settings` on the page request, or loading
   * the settings with their `lpn-page` (which fills the inverse). With neither,
   * the answer is always `undefined` rather than a fetch.
   *
   * Usage:
   *   await lpnPage.settingForCompany(companySetting);      // or
   *   await lpnPage.settingForCompany(companySettingId);
   */
  async settingForCompany(
    companySettingOrId: CompanySetting | string | null | undefined
  ): Promise<CompanyLpnSetting | undefined> {
    if (!companySettingOrId) return undefined;

    const targetId =
      typeof companySettingOrId === 'string' ? companySettingOrId : companySettingOrId.id;

    return loadedSettingsFor(this).find((s) => s.companySetting?.id === targetId);
  }
}

// Never await the hasMany: `async: true` routes through adapterFor, which can only throw here.
// A module function, not a `#private` method: consumers hold these resources as a Proxy.
function loadedSettingsFor(page: LpnPageExtension): CompanyLpnSetting[] {
  // `hasMany()` is not on the generated resource type; narrow rather than `any`.
  const legacy = page as LpnPageExtension & {
    hasMany?: (field: string) => { value: () => unknown } | undefined;
  };
  const settings = legacy.hasMany?.('companyLpnSettings')?.value();
  return Array.isArray(settings) ? (settings as CompanyLpnSetting[]) : [];
}

const Registration = {
  kind: 'object',
  name: 'lpn-page',
  features: LpnPageExtension,
};
export default Registration;
